import {useState, useEffect} from "react";
import {
    Box, Button, Chip, FormControl, Grid,
    MenuItem, Paper, Select, Typography,
} from "@mui/material";
import {Code, Visibility, Edit, ContentCopy, Delete} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {getTemplateVersion, activateTemplateVersion} from "../../../api/templateApi";
import {Template, TemplateVersion} from "../../../types";
import ActivateVersionDialog from "../../../components/ActivateVersionDialog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const formatDate = (date: string) => dayjs.utc(date).local().format('DD-MM-YYYY HH:mm');

const wrapInHtmlDoc = (content: string): string => {
    const trimmed = content.trim().toLowerCase();
    if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html")) return content;
    return `<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />\n</head>\n<body>\n${content}\n</body>\n</html>`;
};

function getDefaultVersion(versions: TemplateVersion[], initialVersion?: number | null): TemplateVersion | null {
    if (initialVersion != null) {
        const found = versions.find(v => v.versionNumber === initialVersion);
        if (found) return found;
    }
    return versions.find(v => v.isActive) || versions[0] || null;
}

interface TabDetailsProps {
    template: Template;
    username: string;
    initialVersion?: number | null;
    onReload: (showVersion?: number) => void;
    onError: (msg: string) => void;
    canEdit: boolean;
    canCreate: boolean;
    canDelete: boolean;
    onEdit: (versionNumber: number | null) => void;
    onDuplicate: () => void;
    onDelete: () => void;
}

const TabDetails = ({template, username, initialVersion, onReload, onError, canEdit, canCreate, canDelete, onEdit, onDuplicate, onDelete}: TabDetailsProps) => {
    const {t} = useTranslation("templates");

    const versions = template.versions || [];
    const defaultVer = getDefaultVersion(versions, initialVersion);

    const [viewMode, setViewMode] = useState<"render" | "html">("render");
    const [selectedVersion, setSelectedVersion] = useState<number | null>(defaultVer?.versionNumber ?? null);
    const [currentVersionData, setCurrentVersionData] = useState<TemplateVersion | null>(defaultVer);
    const [activateOpen, setActivateOpen] = useState(false);
    const [activating, setActivating] = useState(false);

    useEffect(() => {
        if (selectedVersion != null && currentVersionData?.body === undefined) {
            getTemplateVersion(template.name, selectedVersion)
                .then(data => {
                    const full = data.versions?.find(v => v.versionNumber === selectedVersion);
                    if (full) setCurrentVersionData(full);
                })
                .catch(() => onError(t('detailErrors.loadVersion')));
        }
    }, []);

    const handleVersionChange = async (versionNumber: number) => {
        setSelectedVersion(versionNumber);
        try {
            const data = await getTemplateVersion(template.name, versionNumber);
            const full = data.versions?.find(v => v.versionNumber === versionNumber);
            if (full) {
                setCurrentVersionData(full);
            }
        } catch {
            const local = versions.find(v => v.versionNumber === versionNumber);
            if (local) setCurrentVersionData(local);
            else onError(t('detailErrors.loadVersion'));
        }
    };

    const handleActivateVersion = async () => {
        if (selectedVersion === null) return;
        setActivating(true);
        try {
            await activateTemplateVersion(template.name, selectedVersion, username);
            setActivateOpen(false);
            onReload(selectedVersion);
        } catch {
            onError(t('detailErrors.activateVersion'));
        } finally {
            setActivating(false);
        }
    };

    const activeVersion = versions.find(v => v.isActive);
    const isActiveVersion = selectedVersion !== null && activeVersion?.versionNumber === selectedVersion;
    const displaySubject = currentVersionData?.subjectTitle;
    const displayBody = currentVersionData?.body;
    const hasSubject = (template.type === "EMAIL" || template.type === "PUSH") && displaySubject;

    return (
        <>
            {/* Azioni */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, mb: 2}}>
                {canEdit && (
                    <Button variant="contained" color="accent" size="small" startIcon={<Edit/>}
                        onClick={() => onEdit(selectedVersion)}>{t('detailActions.edit')}</Button>
                )}
                {canCreate && (
                    <Button variant="contained" color="accent" size="small" startIcon={<ContentCopy/>}
                        onClick={onDuplicate}>{t('detailActions.duplicate')}</Button>
                )}
                {canDelete && (
                    <Button variant="contained" color="accent" size="small" startIcon={<Delete/>}
                        onClick={onDelete}>{t('detailActions.delete')}</Button>
                )}
            </Box>

            <Paper elevation={1} sx={{padding: '24px'}}>
                <Grid container spacing={3}>
                    {/* Version selector */}
                    <Grid item xs={12}>
                        <Paper variant="outlined">
                            <Box sx={{px:2, py:1.5, display:'flex', alignItems:'center', gap:2, flexWrap:'wrap'}}>
                                <Box sx={{flexShrink:0}}>
                                    <Typography variant="subtitle1" fontWeight={700}>{t('detailSection.versionLabel')}</Typography>
                                    <Typography variant="body2" color="text.secondary">{t('detailSection.subtitle')}</Typography>
                                </Box>
                                <FormControl size="small" sx={{width:420, maxWidth:'100%'}}>
                                    <Select value={selectedVersion ?? ""} 
                                        onChange={(e) => handleVersionChange(Number(e.target.value))}>
                                        {versions.map((v) => (
                                            <MenuItem key={v.versionNumber} value={v.versionNumber}>
                                                <Box>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        <Typography variant="body2">{t('detailSection.versionItem', {number: v.versionNumber})}</Typography>
                                                        {v.isActive && <Chip label={t('detailSection.versionActive')} size="small" color="success" sx={{height: 20, fontSize: '0.7rem'}}/>}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('detailSection.lastModified', {date: formatDate(v.updatedAt || v.createdAt || '')})}
                                                        {v.updatedBy ? ` ${t('detailSection.by')} ${v.updatedBy}` : v.createdBy ? ` ${t('detailSection.by')} ${v.createdBy}` : ''}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {canEdit && !isActiveVersion && selectedVersion !== null && (
                                    <Button variant="contained" size="small" sx={{flexShrink:0}} onClick={() => setActivateOpen(true)}>
                                        {t('detailActions.activateVersion')}
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Subject/Title Section */}
                    {hasSubject && (
                        <Grid item xs={12}>
                            <Paper variant="outlined">
                                <Box sx={{px:2, py:1.5, borderBottom:'1px solid', borderColor:'divider'}}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {template.type === "PUSH" ? t('detailContent.pushTitleLabel') : t('detailContent.subject')}
                                    </Typography>
                                </Box>
                                <Box sx={{px:2, py:2}}>
                                    <Typography variant="body1">
                                        {displaySubject}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* Message Body Section */}
                    <Grid item xs={12}>
                        <Paper variant="outlined">
                            <Box sx={{px:2, py:1.5, borderBottom:'1px solid', borderColor:'divider'}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {t('detailContent.bodyLabel')}
                                    </Typography>
                                    {template.type === "EMAIL" && (
                                        <Button variant="outlined" size="small"
                                            startIcon={viewMode === "html" ? <Visibility/> : <Code/>}
                                            onClick={() => setViewMode(viewMode === "render" ? "html" : "render")}>
                                            {viewMode === "html" ? t('detailContent.showRender') : t('detailContent.showHtml')}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={{px:2, py:2}}>
                                {template.type === "EMAIL" && viewMode === "html" ? (
                                    <Typography variant="body2" component="pre"
                                        sx={{whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem', maxHeight: 400, overflow: 'auto'}}>
                                        {displayBody || t('detailContent.empty')}
                                    </Typography>
                                ) : template.type === "EMAIL" ? (
                                    displayBody ? (
                                        <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff'}}>
                                            <iframe
                                                title="template-preview"
                                                srcDoc={wrapInHtmlDoc(displayBody)}
                                                style={{width: '100%', height: 400, border: 'none'}}
                                                sandbox="allow-same-origin"
                                            />
                                        </Box>
                                    ) : (
                                        <Typography variant="body1">{t('detailContent.empty')}</Typography>
                                    )
                                ) : (
                                    <Typography variant="body1" sx={{maxHeight: 300, overflow: 'auto'}}>
                                        {displayBody?.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').replace(/[ \t]+/g, ' ').replace(/\n\s*\n/g, '\n').trim() || t('detailContent.empty')}
                                    </Typography>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>

            <ActivateVersionDialog open={activateOpen} versionNumber={selectedVersion}
                onClose={() => setActivateOpen(false)} onConfirm={handleActivateVersion} loading={activating}/>
        </>
    );
};

export default TabDetails;
